import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
// ────────────────────────────────────────────────────────────────────────────
// Query keys
// ────────────────────────────────────────────────────────────────────────────
export const roomsKeys = {
    byCode: (code) => ['rooms', 'by-code', code.toUpperCase()],
    byId: (id) => ['rooms', 'by-id', id],
    members: (id) => ['rooms', 'members', id],
    activeGame: (id) => ['rooms', 'active-game', id],
};
// ────────────────────────────────────────────────────────────────────────────
// Queries
// ────────────────────────────────────────────────────────────────────────────
/** Lecture d'une room par son code (cas du flow "rejoindre par code"). */
export function useRoomByCode(code) {
    return useQuery({
        queryKey: code ? roomsKeys.byCode(code) : ['rooms', 'by-code', 'none'],
        enabled: !!code && code.length === 6,
        queryFn: async () => {
            if (!code)
                return null;
            const { data, error } = await supabase
                .from('rooms')
                .select('*')
                .eq('code', code.toUpperCase())
                .maybeSingle();
            if (error)
                throw error;
            return data;
        },
    });
}
/** Lecture d'une room par id. */
export function useRoom(roomId) {
    return useQuery({
        queryKey: roomId ? roomsKeys.byId(roomId) : ['rooms', 'by-id', 'none'],
        enabled: !!roomId,
        queryFn: async () => {
            if (!roomId)
                return null;
            const { data, error } = await supabase.from('rooms').select('*').eq('id', roomId).maybeSingle();
            if (error)
                throw error;
            return data;
        },
    });
}
/**
 * Récupère le `games` actif (status='started') le plus récent pour une room.
 * Utilisé par la Lobby pour rediriger vers /game/:id quand l'hôte lance.
 */
export function useActiveGame(roomId) {
    return useQuery({
        queryKey: roomId ? roomsKeys.activeGame(roomId) : ['rooms', 'active-game', 'none'],
        enabled: !!roomId,
        queryFn: async () => {
            if (!roomId)
                return null;
            const { data, error } = await supabase
                .from('games')
                .select('id, type')
                .eq('room_id', roomId)
                .eq('status', 'started')
                .order('started_at', { ascending: false })
                .limit(1)
                .maybeSingle();
            if (error)
                throw error;
            return data;
        },
    });
}
/** Liste des membres actifs (left_at is null) d'une room avec leurs profiles. */
export function useRoomMembers(roomId) {
    return useQuery({
        queryKey: roomId ? roomsKeys.members(roomId) : ['rooms', 'members', 'none'],
        enabled: !!roomId,
        queryFn: async () => {
            if (!roomId)
                return [];
            const { data, error } = await supabase
                .from('room_players')
                .select(`player_id, is_ready, is_connected, joined_at, left_at, total_score,
           profiles!inner(pseudo, avatar_url, level)`)
                .eq('room_id', roomId)
                .is('left_at', null)
                .order('joined_at', { ascending: true });
            if (error)
                throw error;
            // Aplatit la jointure profile
            return (data ?? []).map((row) => {
                // supabase-js typed la jointure comme un object | objet[]
                const p = row.profiles;
                return {
                    player_id: row.player_id,
                    is_ready: row.is_ready,
                    is_connected: row.is_connected,
                    joined_at: row.joined_at,
                    left_at: row.left_at,
                    total_score: row.total_score,
                    pseudo: p.pseudo,
                    avatar_url: p.avatar_url,
                    level: p.level,
                };
            });
        },
    });
}
export function useCreateRoom() {
    return useMutation({
        mutationFn: async (input = {}) => {
            const { data, error } = await supabase.rpc('create_room', {
                p_mode: input.mode ?? 'tournament',
                p_max_players: (input.max_players ?? 8),
                p_settings: (input.settings ?? {}),
                p_games_planned: (input.games_planned ?? null),
            });
            if (error)
                throw error;
            const row = Array.isArray(data) ? data[0] : data;
            if (!row)
                throw new Error('create_room returned no row');
            return { id: row.id, code: row.code };
        },
    });
}
/** Récupération du tournament d'une room (s'il existe). */
export function useTournament(roomId) {
    return useQuery({
        queryKey: roomId ? ['tournaments', 'by-room', roomId] : ['tournaments', 'by-room', 'none'],
        enabled: !!roomId,
        queryFn: async () => {
            if (!roomId)
                return null;
            const { data, error } = await supabase
                .from('tournaments')
                .select('*')
                .eq('room_id', roomId)
                .maybeSingle();
            if (error)
                throw error;
            return data;
        },
    });
}
/** Classement cumulé (utilisé sur l'écran de cérémonie finale). */
export function useTournamentStandings(roomId) {
    return useQuery({
        queryKey: roomId ? ['tournaments', 'standings', roomId] : ['tournaments', 'standings', 'none'],
        enabled: !!roomId,
        queryFn: async () => {
            if (!roomId)
                return [];
            const { data, error } = await supabase.rpc('get_tournament_standings', { p_room_id: roomId });
            if (error)
                throw error;
            return (data ?? []).map((r) => ({
                rank: Number(r.rank),
                player_id: r.player_id,
                pseudo: r.pseudo,
                avatar_url: r.avatar_url,
                level: Number(r.level),
                total_score: Number(r.total_score),
            }));
        },
    });
}
export function useJoinRoom() {
    return useMutation({
        mutationFn: async (code) => {
            const upper = code.toUpperCase().trim();
            const { data, error } = await supabase.rpc('join_room', { p_code: upper });
            if (error)
                throw mapRoomError(error.message);
            if (!data)
                throw new Error('join_room returned no id');
            return data;
        },
    });
}
export function useLeaveRoom() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (roomId) => {
            const { error } = await supabase.rpc('leave_room', { p_room_id: roomId });
            if (error)
                throw error;
        },
        onSuccess: (_d, roomId) => {
            void qc.invalidateQueries({ queryKey: roomsKeys.members(roomId) });
            void qc.invalidateQueries({ queryKey: roomsKeys.byId(roomId) });
        },
    });
}
export function useTransferHost() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ roomId, newHost }) => {
            const { error } = await supabase.rpc('transfer_host', { p_room_id: roomId, p_new_host: newHost });
            if (error)
                throw error;
        },
        onSuccess: (_d, { roomId }) => {
            void qc.invalidateQueries({ queryKey: roomsKeys.byId(roomId) });
        },
    });
}
export function useKickPlayer() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ roomId, playerId }) => {
            const { error } = await supabase.rpc('kick_player', { p_room_id: roomId, p_player_id: playerId });
            if (error)
                throw error;
        },
        onSuccess: (_d, { roomId }) => {
            void qc.invalidateQueries({ queryKey: roomsKeys.members(roomId) });
        },
    });
}
export function useUpdateRoomSettings() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input) => {
            const { error } = await supabase.rpc('update_room_settings', {
                p_room_id: input.roomId,
                p_mode: (input.mode ?? null),
                p_max_players: (input.max_players ?? null),
                p_settings: (input.settings ?? null),
            });
            if (error)
                throw error;
        },
        onSuccess: (_d, input) => {
            void qc.invalidateQueries({ queryKey: roomsKeys.byId(input.roomId) });
        },
    });
}
export function useCloseRoom() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (roomId) => {
            const { error } = await supabase.rpc('close_room', { p_room_id: roomId });
            if (error)
                throw error;
        },
        onSuccess: (_d, roomId) => {
            void qc.invalidateQueries({ queryKey: roomsKeys.byId(roomId) });
        },
    });
}
/**
 * Toggle is_ready de la ligne `room_players` du joueur courant. Direct via
 * RLS (`room_players_update_self`), pas de RPC nécessaire.
 */
export function useToggleReady() {
    const { user } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ roomId, ready }) => {
            if (!user?.id)
                throw new Error('auth_required');
            const { error } = await supabase
                .from('room_players')
                .update({ is_ready: ready })
                .eq('room_id', roomId)
                .eq('player_id', user.id);
            if (error)
                throw error;
        },
        onSuccess: (_d, { roomId }) => {
            void qc.invalidateQueries({ queryKey: roomsKeys.members(roomId) });
        },
    });
}
/**
 * Met à jour `is_connected` de la ligne courante (heartbeat Presence).
 * Appelée depuis useRoomChannel quand le client se connecte/déconnecte.
 */
export async function setSelfConnected(roomId, userId, connected) {
    await supabase
        .from('room_players')
        .update({ is_connected: connected })
        .eq('room_id', roomId)
        .eq('player_id', userId);
}
// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────
function mapRoomError(message) {
    const lower = message.toLowerCase();
    let code = 'unknown';
    if (lower.includes('room_not_found'))
        code = 'room_not_found';
    else if (lower.includes('room_closed'))
        code = 'room_closed';
    else if (lower.includes('room_full'))
        code = 'room_full';
    else if (lower.includes('not_host'))
        code = 'not_host';
    else if (lower.includes('auth_required'))
        code = 'auth_required';
    const err = new Error(message);
    err.code = code;
    return err;
}
